/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import {
  EVENT_NAME_AssetEnteredLocation,
  EVENT_NAME_AssetExitedLocation,
  EVENT_NAME_AssetMovement,
  EVENT_NAME_PersonMovement,
  EVENT_NAME_PersonExitedLocation,
  EVENT_NAME_PersonEnteredLocation,
  EVENT_TYPE_Asset,
  EVENT_TYPE_Person,
  EVENT_PROP_Asset,
  EVENT_PROP_Person,
} from "../../app-constants";
import { EventInstance } from "../../models/event-instance";
import { InstanceData } from "../../models/instance-data";
import { LocationInstance } from "../../models/location-instance";
import { ObjectInstance } from "../../models/object-instance";
import { Point } from "../../models/point";
import { LocationWebSocketService } from "../../services/location-web-socket.service";
import { ObjectService } from "../../services/object.service";
import { EventPointUi } from "./ui-models/event-point-ui";
import { LocationUi } from "./ui-models/location-ui";
import { ObjectUi } from "./ui-models/object-ui";
import { PersonUI, Breadcrumb } from "./ui-models/person-ui";
import { LocationService } from "../../services/location.service";

import { Component, OnInit, EventEmitter, Output, Input, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import * as createjs from 'createjs-module';
import { ZoneInstance } from "../../models/zone-instance";
import { MathModule } from "../../shared/math.module";

import Tween = createjs.Tween;
import { ServerObjectService } from "../../services/server-object.service";
import { ImageService } from "app/services/image.service";
import { Subject, Subscription } from "rxjs";
import { EventData } from "app/models/event-data";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "../simple-success-error-dialog-box/simple-success-error-dialog-box.component";

declare var $: any;
export const RESULTS_PER_PAGE = 15;
const DEFAULT_TWEEN_DELAY = 500;
const SCALE_INDEX_MULTIPLIER = .05;
const MIN_SCALE_INDEX = 1;
const SCALE_INDEX_100_PERCENT = 20;
const MAX_SCALE_INDEX = 200;
const ZOOM_ZONE_LEVEL = 1.25;
const VERTICAL_PADDING = 0;

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html'
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {

  /** Reference to the SimpleSucces sErrorDialog used to display error */
@ViewChild('successErrorDialog') 
simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  static mapComponentCounter = 0; 
  mapCanvasId = null;
  tweenDelay = DEFAULT_TWEEN_DELAY;
  resultsPerPageValues = RESULTS_PER_PAGE;

  _Object = Object;
  _locationId = -1;

  @Input()
  set location(location: LocationInstance) {
    // If the new location is null or not the same as the previous location...
    if (location == null || location.id != this._locationId) {
      if (!this.keepVIPAfterLocationLoad) {
        this.removeAllPeopleAndAssets();
      }

      let updateLocation = true;
      if (location == null) {
        if (this.useVIP) {
          // Make sure that every VIP is also null....
          for(const asset of Object.keys(this.mAssetsMap)) {
            if (this.assetsVIP[asset]) {
              const objectUI = this.mAssetsMap[asset];
              if (objectUI && objectUI.mObject.locationInstance) {
                updateLocation = false;
              }
            }
          }
          for(const person of Object.keys(this.mPeopleMap)) {
            if (this.peopleVIP[person]) {
              const personUI = this.mPeopleMap[person];
              if (personUI && personUI.mObject.locationInstance) {
                updateLocation = false;
              }
            }
          }

          if (updateLocation) {
            console.log("setting location to NULL in map component");
          }
        }
      } else {
        console.log("setting location to " + location.name + " in map component");
      }

      if (updateLocation) {
        this.updateLocation(location);
      }
    }
  }

  @Input()
  set eventPoint(eventPoint: Point) {
    this.drawEventPointUI((eventPoint) ? new EventPointUi().setPoint(eventPoint) : null);
  }

  @Input() overrideDefaults = false;

  @Input()
  set displayZones(displayZones: boolean) {
    this._displayZones = displayZones;

    if (this._displayZones) {
      this.addZonesToDisplay();
    } else {
      this.removeZonesFromDisplay();
    }
  }

  get displayZones(): boolean {
    return this._displayZones;
  }

  @Input()
  set displaySublocations(displaySublocations: boolean) {
    this._displaySublocations = displaySublocations;

    if (this._displaySublocations) {
      this.addSublocationsToDisplay();
    } else {
      this.removeSublocationsFromDisplay();
    }
  }

  get displaySublocations(): boolean {
    return this._displaySublocations;
  }


  @Input()
  set showBreadcrumbs(showBreadcrumbs: boolean) {
    //console.log("showBreadcrumbs(" + showBreadcrumbs + ")");
    this._showBreadcrumbs = showBreadcrumbs;
    this.activateOrDeactivateBreadcrumbs();
  }

  get showBreadcrumbs(): boolean {
    return this._showBreadcrumbs;
  }

  @Input()
  set displayPerimeters(showPerimeters: boolean) {
    //console.log("displayPerimeters(" + showPerimeters + ")");
    this._showPerimeters = showPerimeters;
    this.activateOrDeactivatePerimeters();
  }

  get displayPerimeters(): boolean {
    return this._showPerimeters;
  }

  @Input()
  set showZoneNames(showZoneNames: boolean) {
    this._showZoneNames = showZoneNames;
    if (this._showZoneNames) {
      this.addZoneNamesToDisplay();
    } else {
      this.removeZoneNamesFromDisplay();
    }
  }

  get showZoneNames(): boolean {
    return this._showZoneNames;
  }

  @Input()
  set showSublocationNames(showSublocationNames: boolean) {
    this._showSublocationNames = showSublocationNames;
    if (this._showSublocationNames) {
      this.addSublocationNamesToDisplay();
    } else {
      this.removeSublocationNamesFromDisplay();
    }
  }

  get showSublocationNames(): boolean {
    return this._showSublocationNames;
  }


  @Input()
  set displayAssets(showAssets: boolean) {
    //console.log("displayAssets(" + showAssets + ")");
    this._showAssets = showAssets;
    if (this._showAssets) {
      this.refreshAssetsInLocation();
    } else {
      this.removeAllAssets();
    }
  }

  get displayAssets(): boolean {
    return this._showAssets;
  }

  @Input()
  set displayPeople(showPeople: boolean) {
    //console.log("displayPeople(" + showPeople + ")");
    this._showPeople = showPeople;
    if (this._showPeople) {
      this.refreshPeopleInLocation();
    } else {
      this.removeAllPeople();
    }
  }

  get displayPeople(): boolean {
    return this._showPeople;
  }

  @Input()
  set mapVIPs(vipList: ObjectInstance[]) {
    if (vipList && vipList.length > 0) {
      for(const objectInstance of vipList) {
        if (objectInstance.object.category == "ASSET") {
          this.assetsVIP[objectInstance.id] = objectInstance;
          this._showAssets = true;
        } else {
          this.peopleVIP[objectInstance.id] = objectInstance;
          this._showPeople = true;
        }
      }
      this.useVIP = true;
      //onsole.log("Set mapVIPs");
    }
  }

  @Input() zoomToZone = null;

  @Input() displayMenu = false;

  @Input() useShift = true;

  @Input() mapInModal = false;

  @Output() mappingEvent: EventEmitter<MappingEvent> = new EventEmitter();

  @Output() assetPositionChangedEvent: EventEmitter<ObjectInstance> = new EventEmitter();

  //Canvas Element being drawn
  mCanvasElement: HTMLCanvasElement;

  //createjs.Ticker "tick" event listener, used to update the Stage.
  mOnTickFunction: Function;

  //Stage where everything is drawn
  mStage: createjs.Stage;

  //Variables used in Zooming
  mStageScaleIndex: number = SCALE_INDEX_100_PERCENT;
  initialScale: number;

  //Variables used in Panning
  mCurrentPos = new Point();

  //Flag used to cancel any click events that occur... Set when performing various operations, reset on each mouse down unless performing an operation (panning/drawing)
  mCancelClickEvent = false;

  //Location currently being drawn on the Map
  mLocationUi: LocationUi = null;

  //Event Point currently being drawn on the Map
  mEventPointUi: EventPointUi = null;

  //Variables related to monitoring/tracking Assets/People in the Location
  mLocationWsSubject: Subject<MessageEvent>;
  mLocationWsSubscription: Subscription;
  mPeopleMap: { [propKey: number]: PersonUI } = {};
  mPeopleHideEmitterMap: { [propKey: number]: EventEmitter<boolean> } = {};   //Used to hide the Popup Dialog
  mAssetsMap: { [propKey: number]: ObjectUi } = {};
  mAssetHideEmitterMap: { [propKey: number]: EventEmitter<boolean> } = {};  //Used to hide the Popup Dialog
  mPerimetersMap: { [propKey: number]: createjs.Shape } = {};

  //Timer used on Window Resize.  Delays the resizing of the Canvas until the User has stopped resizing the Window for half a second.  Done to improve performance/limit the number of times the Canvas is resized.
  mCanvasResizeTimer = null;

  // breadcrumb information
  private _showBreadcrumbs = false;
  breadcrumbTrailSize = 20;

  // Animate icon between last point
  shouldTween = true;

  // Track people or not
  trackPeopleAndAssets = true;
  useVIP = false; // if useVIP is set to TRUE, then only assets and people in the assetsVIP and peopleVIP lists will be shown
  private assetsVIP = {};
  private peopleVIP = {};

  // Show zones or not
  private _displayZones = false;
  zoneUIs: ZoneUi[] = [];

  // show sublocations or not
  private _displaySublocations = false;
  sublocationUIs: SublocationUi[] = [];

  // Variables for setters/getters
  private _showZoneNames = false;
  private _showSublocationNames = false;
  private _showPerimeters = false;
  private _showAssets = false;
  private _showPeople = false;

  // Variable used to determine to keep VIP or not after loading location
  keepVIPAfterLocationLoad = false;

  defaultMapping = null;
  settingsDefaultService = {};
  serverObjectInstanceId;
  mappingServerObjectInstance;

  constructor(
    private mLocationWebSocketService: LocationWebSocketService,
    private mObjectService: ObjectService,
    private locationService: LocationService,
    private serverObjectService: ServerObjectService,
    private mImageService: ImageService
  ) {
  }

  ngOnInit(): void {
    if (this.overrideDefaults == true) {
      return;
    }

    // Grab the map setting defaults (only if we are not overriding the defaults)
    this.serverObjectService.getAllServerObjects().then(
      response => {
        for (const object of response) {
          if (object.name == "Edgefinity Server") {
            this.serverObjectService.getAllServerObjectInstances().then(
              responseAllServerObjects => {
                for (const objectInstance of responseAllServerObjects) {
                  if (objectInstance.serverObject && objectInstance.serverObject.id == object.id) {
                    const serverObjectInstance = objectInstance;
                    this.serverObjectInstanceId = serverObjectInstance.id;

                    for(const dataElement of serverObjectInstance.data) {
                      if (dataElement.property && dataElement.property.property) {
                        if (dataElement.value) {
                          if (dataElement.object_value.server_object_reference) {
                            this.settingsDefaultService[dataElement.property.property] = dataElement.object_value.server_object_reference;
                          } else if (dataElement.object_value.service_reference) {
                            this.settingsDefaultService[dataElement.property.property] = dataElement.object_value.service_reference;
                          } else {
                            this.settingsDefaultService[dataElement.property.property] = dataElement.object_value;
                          }
                        }
                      }
                    }

                    break;
                  }
                }

                this.defaultMapping = this.settingsDefaultService['mapping'];

                if (this.defaultMapping) {
                  // Found the email service

                  this.serverObjectService.getServerObjectInstanceById(this.defaultMapping.id).then(response2 => {
                      this.mappingServerObjectInstance = response2;
                      this.setMappingDefaults();
                    },
                    error => {
                      this.handleError(error);
                    });
                }
              },
              error => {
                console.error("Could not find the Edgefinity Server Instance");
              }
            );
            break;
          }
        }
      },
      error => {
        console.error("Could not find the Edgefinity Server Object");
      }
    )
  }

  ngAfterViewInit(): void {
    //Get the Canvas Element used by the Stage where everything is drawn
    this.mCanvasElement = document.getElementById(this.getMapCanvasId()) as HTMLCanvasElement;

    //Resize the Canvas so that it has a 1:1 ratio between actual pixels and virtual pixels
    this.resizeCanvas();

    //Create the createjs.Stage where everything gets drawn
    this.mStage = new createjs.Stage(this.mCanvasElement);

    //Attach a Function to execute on each createjs.Ticker "tick", used to redraw the map
    const component = this;
    this.mOnTickFunction = createjs.Ticker.on("tick", function(): void {
      component.mStage.update();
    });
  }

  ngOnDestroy(): void {
    //Stop tracking any events in the Location
    this.stopTracking();

    //Clean up/remove the function attached to the "tick" events of the createjs.Ticker, used to redraw the map... The map is gone!
    createjs.Ticker.off("tick", this.mOnTickFunction);
  }

  setMappingDefaults(): void {
    console.log("Set Mapping Defaults");
    this.displayAssets = this.mappingServerObjectInstance.data.find(d => d.property.property == "assets").object_value;
    this.displayPeople = this.mappingServerObjectInstance.data.find(d => d.property.property == "people").object_value;
    this.displayPerimeters = this.mappingServerObjectInstance.data.find(d => d.property.property == "perimeters").object_value;
    this.showBreadcrumbs = this.mappingServerObjectInstance.data.find(d => d.property.property == "breadcrumbs").object_value;
    this.displayZones = this.mappingServerObjectInstance.data.find(d => d.property.property == "zones").object_value;
    this.showZoneNames = this.mappingServerObjectInstance.data.find(d => d.property.property == "zoneNames").object_value;
    this.displaySublocations = this.mappingServerObjectInstance.data.find(d => d.property.property == "sublocations").object_value;
    this.showSublocationNames = this.mappingServerObjectInstance.data.find(d => d.property.property == "sublocationNames").object_value;
  }

  /*
   * Set the Canvas's Height/Width attributes, to that of the pixel height/width.
   * This keeps the Canvas at the same scale as the rest of the page since style width/height
   * are 100% to use up all available space.
   * This gets called on the initial display, and it is tied to any window resizing to fix the scaling
   * in case the size changes.
   */
  private resizeCanvas(): void {
    this.mCanvasElement.height = this.mCanvasElement.clientHeight;
    this.mCanvasElement.width = this.mCanvasElement.clientWidth;
  }

  onWindowResize(): void {
    if (this.mCanvasResizeTimer != null) {
      clearTimeout(this.mCanvasResizeTimer);
    }
    const component = this;
    this.mCanvasResizeTimer = setTimeout(function(): void {
      component.resizeCanvas();
    }, 500);
  }

  onMouseWheel(event: WheelEvent): boolean {
    //Scale the Image based on how hard/fast the User scrolled the wheel
    const numberOfScrollIns = event.deltaY / 100;
    if (!this.useShift || (this.useShift && event.shiftKey)) {

      if (numberOfScrollIns >= 0) {
        this.zoomIn(numberOfScrollIns);
      } else {
        this.zoomOut(-numberOfScrollIns);
      }
    } else {
      console.log("Trying to onMouseWheel without shift held");
    }
    return false;
  }

  onMouseDown(event: MouseEvent): void {
    //When the shift key is being held, that indicates the User wants to pan the Image... Store the currently X/Y of the cursor for comparisons
    if (!this.useShift || (this.useShift && event.shiftKey)) {
      this.mCurrentPos = new Point();
      this.mCurrentPos.x = event.pageX;
      this.mCurrentPos.y = event.pageY;
      if (this.useShift && event.shiftKey) {
        this.mCancelClickEvent = true;
      }
    } else {
      console.log("Trying to onMouseDown without shift held");

    }
  }

  onMouseMove(event: MouseEvent): void {
    //Handle Panning... Continue panning the image as long as shift is being held
    if (this.mCurrentPos != null) {
      if (!this.useShift || (this.useShift && event.shiftKey)) {
        console.log("MOVE_STAGE from onMouseMove");
        this.moveStage(event.pageX - this.mCurrentPos.x, event.pageY - this.mCurrentPos.y);
        this.mCurrentPos.x = event.pageX;
        this.mCurrentPos.y = event.pageY;
      } else {
        this.mCurrentPos = null;
        console.log("Trying to onMouseMove without shift held");
      }
    }
  }

  moveStage(pixelsX: number, pixelsY: number): void {
    //console.log("Moving pixelsX=" + pixelsX + ", pixelsY=" + pixelsY);
    this.mStage.x = this.mStage.x + pixelsX;
    this.mStage.y = this.mStage.y + pixelsY;
  }

  onMouseUp(event: MouseEvent): void {
    //If we are panning, stop panning
    (this.mCurrentPos) ? this.mCurrentPos = null : this.mCancelClickEvent = false;
  }

  onMouseLeave(event: MouseEvent): void {
    //If we are panning, stop panning
    if (this.mCurrentPos != null) {
      this.mCurrentPos = null;
    }
  }

  async updateLocation(location: LocationInstance): Promise<void> {
    this._locationId = (location == null) ? -1 : location.id;
    await this.drawLocationUI((location) ? new LocationUi().setLocation(location) : null);

    // If we are tracking people and assets, load up all the assets and people
    if (location != null && this.trackPeopleAndAssets) {
      await this.refreshAssetsInLocation();
      await this.refreshPeopleInLocation();
    } else {

    }

  }

  updateLocationToLocationId(locationId: number): void {
    this.locationService
      .getLocationInstanceById(locationId)
      .then(
        response => {
          this.location = response;
        }
      );
  }

  removeZonesFromDisplay(): void {
    //Add or remove zones from display
    if (this.zoneUIs != null && this.zoneUIs.length > 0) {
      this.zoneUIs.forEach(zoneUI => {
          this.removeZoneFromDisplay(zoneUI);
      });
    }
  }

  addZonesToDisplay(): void {
    //Add or remove zones from display
    if (this.zoneUIs != null && this.zoneUIs.length > 0) {
      this.zoneUIs.forEach(zoneUI => {
        this.addZoneToDisplay(zoneUI);
      });
    }
  }

  removeSublocationsFromDisplay(): void {
    //Add or remove zones from display
    if (this.sublocationUIs != null && this.sublocationUIs.length > 0) {
      this.sublocationUIs.forEach(sublocationUI => {
        this.removeSublocationFromDisplay(sublocationUI);
      });
    }
  }

  addSublocationsToDisplay(): void {
    //Add or remove zones from display
    if (this.sublocationUIs != null && this.sublocationUIs.length > 0) {
      this.sublocationUIs.forEach(sublocationUI => {
        this.addSublocationToDisplay(sublocationUI);
      });
    }
  }

  removeZoneNamesFromDisplay(): void {
    //Add or remove zone names from display
    if (this.zoneUIs != null && this.zoneUIs.length > 0) {
      this.zoneUIs.forEach(zoneUI => {
        this.removeZoneNameFromDisplay(zoneUI);
      });
    }
  }

  addZoneNamesToDisplay(): void {
    //Add or remove zone names from display
    if (this.zoneUIs != null && this.zoneUIs.length > 0) {
      this.zoneUIs.forEach(zoneUI => {
        this.addZoneNameToDisplay(zoneUI);
      });
    }
  }

  removeSublocationNamesFromDisplay(): void {
    //Add or remove sublocation names from display
    if (this.sublocationUIs != null && this.sublocationUIs.length > 0) {
      this.sublocationUIs.forEach(sublocationUI => {
        this.removeSublocationNameFromDisplay(sublocationUI);
      });
    }
  }

  addSublocationNamesToDisplay(): void {
    //Add or remove sublocation names from display
    if (this.sublocationUIs != null && this.sublocationUIs.length > 0) {
      this.sublocationUIs.forEach(sublocationUI => {
        this.addSublocationNameToDisplay(sublocationUI);
      });
    }
  }

  getLocationId(): number {
    if (this.mLocationUi && this.mLocationUi.mLocation) {
      return this.mLocationUi.mLocation.id;
    } else {
      return null;
    }
  }

  private async drawLocationUI(ui: LocationUi): Promise<void> {
    console.log("async drawLocationUi() - Location: " + (ui == null ? "*NULL-UI*" : ui.mLocation == null ? "*NULL-LOCATION" : (ui.mLocation.id + "/" + ui.mLocation.name)));
    //Stop tracking the old Location
    if (this.trackPeopleAndAssets) {
      this.stopTracking();
    }

    if (ui == null || ui.mLocation == null) {
      this.removeAllPeopleAndAssets();
    }

    //Erase the old Location
    await this._eraseLocationUi(this.mLocationUi);

    // Remove zone
    this.removeZonesFromDisplay();
    this.removeZoneNamesFromDisplay();
    this.zoneUIs = [];

    //Set the Location being represented on the Map
    this.mLocationUi = ui;

    //Draw the new Location
    await this._drawLocationUi(ui);

  }

  private async _drawLocationUi(ui: LocationUi): Promise<void> {
    if (ui != null && ui.mLocation != null && ui.mLocation.image != null) {
      //Create the Image for the Location
      const image = document.createElement('img');
      image.crossOrigin = 'Anyonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
      image.src = this.mImageService.getImageSourceForImageId(ui.mLocation.image.id);
      ui.mImage = new createjs.Bitmap(image);

      const component = this;
      ui.mImage.image.onload = function(): void {
        //Add the floor plan to the Stage being drawn
        component.mStage.addChildAt(ui.mImage, 0);

        //Scale the Stage so that the entire Image can be show in the display
        {
          const horizontalScale = component.mCanvasElement.offsetWidth / ui.mImage.image.width;
          const verticalScale = (component.mCanvasElement.offsetHeight - VERTICAL_PADDING) / ui.mImage.image.height;
          const scaleIndex = Math.floor((horizontalScale < verticalScale ? horizontalScale : verticalScale) / SCALE_INDEX_MULTIPLIER);
          component.setScaleIndex(scaleIndex, true);
        }

        //Center the Image in the center of the display
        {
          const diffX = component.mCanvasElement.offsetWidth - (component.mStage.scaleX * ui.mImage.image.width);
          const diffY = component.mCanvasElement.offsetHeight - VERTICAL_PADDING - (component.mStage.scaleY * ui.mImage.image.height);
          component.mStage.x = diffX / 2;
          component.mStage.y = diffY / 2;
        }

        //Start tracking at the new Location
        if (component.trackPeopleAndAssets) {
          component.startTracking();
        }

        component.locationService
          .getLocationInstanceById(ui.mLocation.id)
          .then(
            response => {
              //Clear the old zone information
              if (component.zoneUIs.length > 0) {
                component.zoneUIs.forEach(zoneUI => {
                  component.mStage.removeChild(zoneUI.mShape);
                });
                component.zoneUIs = [];
              }

              //Add the zone information
              if (response.zones != null) {
                response.zones.forEach(zone => {
                  const zoneUI = new ZoneUi();
                  zoneUI.mZone = zone;

                  if (component._displayZones) {
                    component.addZoneToDisplay(zoneUI);
                  }

                  if (component._showZoneNames) {
                    component.addZoneNameToDisplay(zoneUI);
                  }

                  component.zoneUIs.push(zoneUI);
                });
              }

              //Clear the old sublocation information
              if (component.sublocationUIs.length > 0) {
                component.sublocationUIs.forEach(sublocationUI => {
                  component.mStage.removeChild(sublocationUI.mShape);
                });
                component.sublocationUIs = [];
              }

              //Add the sublocation information
              if (response.childLocations != null) {
                response.childLocations.forEach(sublocation => {
                  const sublocationUi = new SublocationUi();
                  sublocationUi.mSublocation = sublocation;

                  if (component._displaySublocations) {
                    component.addSublocationToDisplay(sublocationUi);
                  }

                  if (component._showSublocationNames) {
                    component.addSublocationNameToDisplay(sublocationUi);
                  }

                  component.sublocationUIs.push(sublocationUi);
                });
              }

              // Zoom To Zone (if necessary)
              // If we have a zone, we want to zoom to zone
              if (component.zoomToZone) {
                console.log("Executing zoom to zone on zone: " + component.zoomToZone.id);
                for(const zone of component.zoneUIs) {
                  if (zone.mZone.id == component.zoomToZone.id) {
                    component.executeZoomToZone(zone.mZone);
                    break;
                  }
                }
              }

              component.mappingEvent.emit(MappingEvent.LOCATION_LOADED);

            });


      }
    }
  }


  addZoneToDisplay(ui: ZoneUi): void {
    if (ui.mShape == null) {
      ui.mShape = new createjs.Shape();
      ui.mShape.alpha = 0.5;
      ui.mShape.graphics.setStrokeStyle(1).beginStroke("black").beginFill(this.rgbToHex(ui.mZone.color.red, ui.mZone.color.green, ui.mZone.color.blue));
      let startX: number = null, startY: number = null;
      ui.mZone.perimeterPoints.forEach(point => {
        const x = this.translatePositionValue(point.x);
        const y = this.translatePositionValue(point.y);
        if (startX == null) {
          ui.mShape.graphics.moveTo(x, y);
          startX = x;
          startY = y;
        } else {
          ui.mShape.graphics.lineTo(x, y);
        }
      });
      ui.mShape.graphics.lineTo(startX, startY);
      ui.mShape.graphics.endFill();
      this.mStage.addChildAt(ui.mShape, 1);
    }
  }

  removeZoneFromDisplay(ui: ZoneUi): void {
    if (ui.mShape != null) {
      this.mStage.removeChild(ui.mShape);
      ui.mShape = null;
    }
  }

  addSublocationToDisplay(ui: SublocationUi): void {
    if (ui.mShape == null) {
      ui.mShape = new createjs.Shape();
      ui.mShape.alpha = 0.5;
      ui.mShape.graphics.setStrokeStyle(1).beginStroke("black").beginFill(this.rgbToHex(ui.mSublocation.color.red, ui.mSublocation.color.green, ui.mSublocation.color.blue));
      let startX: number = null, startY: number = null;
      ui.mSublocation.perimeterPoints.forEach(point => {
        const x = this.translatePositionValue(point.x);
        const y = this.translatePositionValue(point.y);
        if (startX == null) {
          ui.mShape.graphics.moveTo(x, y);
          startX = x;
          startY = y;
        } else {
          ui.mShape.graphics.lineTo(x, y);
        }
      });
      ui.mShape.graphics.lineTo(startX, startY);
      ui.mShape.graphics.endFill();
      this.mStage.addChildAt(ui.mShape, 1);
    }
  }

  removeSublocationFromDisplay(ui: SublocationUi): void {
    if (ui.mShape != null) {
      this.mStage.removeChild(ui.mShape);
      ui.mShape = null;
    }
  }

  addZoneNameToDisplay(ui: ZoneUi): void {
    if (ui.mText == null) {
      let fontSize = 48;
      if (this.initialScale > .5) {
        if (this.initialScale > .6) {
          if (this.initialScale > .74) {
            fontSize = 16;
          } else {
            fontSize = 24;
          }
        } else {
          fontSize = 36;
        }
      }
      console.log("FONT SIZE: " + fontSize + ", initialScale: " + this.initialScale);

      let zoneNameColor = null;
      if (ui.mZone.textColor) {
        zoneNameColor = this.rgbToHex(ui.mZone.textColor.red, ui.mZone.textColor.green, ui.mZone.textColor.blue);
      } else {
        const luma = (0.2126 * ui.mZone.color.red) + (0.7152 * ui.mZone.color.green) + (0.0722 * ui.mZone.color.blue);
        if (luma < 40) {
          // If the color is darker than normal, the text will be lighter
          zoneNameColor = this.rgbToHex(Math.floor(ui.mZone.color.red + (.75 * (255 - ui.mZone.color.red))),
            Math.floor(ui.mZone.color.green + (.75 * (255 - ui.mZone.color.green))),
            Math.floor(ui.mZone.color.blue + (.75 * (255 - ui.mZone.color.blue))));
        } else {
          // If the color is lighter than normal, the text will be darker
          zoneNameColor = this.rgbToHex(Math.floor(.25 * ui.mZone.color.red),
            Math.floor(.25 * ui.mZone.color.green),
            Math.floor(.25 * ui.mZone.color.blue));
        }
      }
      ui.mText = new createjs.Text(ui.mZone.name, fontSize + "px Arial", zoneNameColor);
      ui.mText.alpha = 1.0;

      const midPoint = this.getZoneParameters(ui.mZone).midPointName;

      ui.mText.x = this.translatePositionValue(midPoint.x);
      ui.mText.y = this.translatePositionValue(midPoint.y);
      ui.mText.textBaseline = "alphabetic";
      this.mStage.addChildAt(ui.mText, this.mStage.numChildren);
    }
  }

  removeZoneNameFromDisplay(ui: ZoneUi): void {
    if (ui.mText != null) {
      this.mStage.removeChild(ui.mText);
      ui.mText = null;
    }
  }

  addSublocationNameToDisplay(ui: SublocationUi): void {
    if (ui.mText == null) {

      let fontSize = 48;
      if (this.initialScale > .5) {
        if (this.initialScale > .6) {
          if (this.initialScale > .74) {
            fontSize = 16;
          } else {
            fontSize = 24;
          }
        } else {
          fontSize = 36;
        }
      }

      let sublocationNameColor = null;
      if (ui.mSublocation.textColor) {
        sublocationNameColor = this.rgbToHex(ui.mSublocation.textColor.red, ui.mSublocation.textColor.green, ui.mSublocation.textColor.blue);
      } else {
        const luma = (0.2126 * ui.mSublocation.color.red) + (0.7152 * ui.mSublocation.color.green) + (0.0722 * ui.mSublocation.color.blue);
        if (luma < 40) {
          // If the color is darker than normal, the text will be lighter
          sublocationNameColor = this.rgbToHex(Math.floor(ui.mSublocation.color.red + (.75 * (255 - ui.mSublocation.color.red))),
            Math.floor(ui.mSublocation.color.green + (.75 * (255 - ui.mSublocation.color.green))),
            Math.floor(ui.mSublocation.color.blue + (.75 * (255 - ui.mSublocation.color.blue))));
        } else {
          // If the color is lighter than normal, the text will be darker
          sublocationNameColor = this.rgbToHex(Math.floor(.25 * ui.mSublocation.color.red),
            Math.floor(.25 * ui.mSublocation.color.green),
            Math.floor(.25 * ui.mSublocation.color.blue));
        }
      }
      ui.mText = new createjs.Text(ui.mSublocation.name, fontSize + "px Arial", sublocationNameColor);
      ui.mText.alpha = 1.0;

      const midPoint = this.getSublocationParameters(ui.mSublocation).midPointName;

      ui.mText.x = this.translatePositionValue(midPoint.x);
      ui.mText.y = this.translatePositionValue(midPoint.y);
      ui.mText.textBaseline = "alphabetic";
      this.mStage.addChildAt(ui.mText, this.mStage.numChildren);
    }
  }

  removeSublocationNameFromDisplay(ui: SublocationUi): void {
    if (ui.mText != null) {
      this.mStage.removeChild(ui.mText);
      ui.mText = null;
    }
  }



  translatePositionValue(posVal: number): number {
    if (this.mLocationUi.mLocation == null || this.mLocationUi.mLocation.scale == null) {
      return posVal;
    } else {
      return posVal * this.mLocationUi.mLocation.scale;
    }
  }

  rgbToHex(r: number, g: number, b: number):string {
    if (r > 255) { r = 255; }
    if (g > 255) { g = 255; }
    if (b > 255) { b = 255; }
    return "#" + this.colorToHex(r) + this.colorToHex(g) + this.colorToHex(b);
  }

  colorToHex(c: number): string {
    const h = c.toString(16);
    return h.length == 1? "0" + h : h;
  }

  private async _eraseLocationUi(ui: LocationUi): Promise<void> {
    if (ui != null && ui.mImage != null) {
      this.mStage.removeChild(ui.mImage);
      ui.mImage = null;
    }
  }

  // Track individual people
  addPersonToVIP(person: ObjectInstance): void {
    if (!this.mPeopleMap[person.id]) {
      this.mPeopleMap[person.id] = new PersonUI();
      // Grab this person's information
      this.mPeopleMap[person.id].mPerson = person;
      this.drawPersonUi(this.mPeopleMap[person.id]);
    }

    this.peopleVIP[person.id] = true;
    this.drawPersonUi(this.mPeopleMap[person.id]);
  }

  removePersonFromVIP(personId: number): void {
    if (this.mPeopleMap[personId]) {
      this.peopleVIP[personId] = false;
      this.mStage.removeChild(this.mPeopleMap[personId].mIcon);
      this.mPeopleMap[personId].mIcon = null;
      this.mStage.update();
    }

    // If there are no people or assets left to display, set the location to null
    let foundPerson = false;
    for(const person of Object.keys(this.peopleVIP)) {
      if (this.peopleVIP[person]) {
        foundPerson = true;
        break;
      }
    }

    let foundAsset = false;
    for(const asset of Object.keys(this.assetsVIP)) {
      if (this.assetsVIP[asset]) {
        foundAsset = true;
        break;
      }
    }

    if (!foundAsset && !foundPerson) {
      this.location = null;
    }

  }

  removeAllVIPs(): void {
    for(const person of Object.keys(this.mPeopleMap)) {
      this.mPeopleMap[person].display = false;
      this.mStage.removeChild(this.mPeopleMap[person].mIcon);
      this.mPeopleMap[person].mIcon = null;
    }

    for(const asset of Object.keys(this.mAssetsMap)) {
      this.mAssetsMap[asset].display = false;
      this.mStage.removeChild(this.mAssetsMap[asset].mIcon);
      this.mAssetsMap[asset].mIcon = null;
    }

    this.mStage.update();
  }

  // Track individual assets
  addAssetToVIP(asset: ObjectInstance): void {
    this.mAssetsMap[asset.id] = new ObjectUi();
    this.mAssetsMap[asset.id].mObject = asset;
    this.assetsVIP[asset.id] = true;
    this.drawObjectUi(this.mAssetsMap[asset.id]);
  }

  removeAssetFromVIP(assetId: number): void {
    if (this.mAssetsMap[assetId]) {
      this.assetsVIP[assetId] = false;
      this.mStage.removeChild(this.mAssetsMap[assetId].mIcon);
      this.mAssetsMap[assetId].mIcon = null;
      this.mStage.update();
    }

    // If there are no people or assets left to display, set the location to null
    let foundPerson = false;
    for(const person of Object.keys(this.peopleVIP)) {
      if (this.peopleVIP[person]) {
        foundPerson = true;
        break;
      }
    }

    let foundAsset = false;
    for(const asset of Object.keys(this.assetsVIP)) {
      if (this.assetsVIP[asset]) {
        foundAsset = true;
        break;
      }
    }

    if (!foundAsset && !foundPerson) {
      this.location = null;
    }
  }

  private stopTracking(): void {
    if (this.mLocationWsSubject != null) {
      this.mLocationWsSubject.unsubscribe();
      this.mLocationWsSubject = null;
    }
    if (this.mLocationWsSubscription != null) {
      this.mLocationWsSubscription.unsubscribe();
      this.mLocationWsSubscription = null;
    }
  }

  private startTracking(): void {
    this.stopTracking();

    if (this.mLocationUi != null && this.mLocationUi.mLocation != null && this.mLocationUi.mLocation.id != null) {
      console.log("Creating Location WS for Location: " + this.mLocationUi.mLocation.id + "/" + this.mLocationUi.mLocation.name);
      this.mLocationWsSubject = this.mLocationWebSocketService.createSubjectForLocationInstanceId(this.mLocationUi.mLocation.id);
      this.mLocationWsSubscription = this.mLocationWsSubject.subscribe(
        message => {
          //Parse the message as an EventInstance
          const event = JSON.parse(message.data) as EventInstance;
          if (event != null && event.event != null) {
            //Currently only handling tracking of People/Assets in here... So only handle events for those Objects
            if (event.event.type == EVENT_TYPE_Asset) {
              console.log("Handling EVENT_TYPE_Asset");
              if (this.useVIP) {
                const datum = event.data.find(d => d.property.property == EVENT_PROP_Asset);
                if (datum) {
                  const assetId = Number.parseInt(datum.value, 10);
                  if (this.assetsVIP[assetId]) {
                    this.handleAssetEvent(event);
                  }
                }
              } else {
                this.handleAssetEvent(event);
              }
            } else if (event.event.type == EVENT_TYPE_Person) {
              console.log("Handling EVENT_TYPE_Person");
              if (this.useVIP) {
                const datum = event.data.find(d => d.property.property == EVENT_PROP_Person);
                if (datum) {
                  const peopleId = Number.parseInt(datum.value, 10);
                  if (this.peopleVIP[peopleId]) {
                    this.handlePersonEvent(event);
                  }
                }
              } else {
                this.handlePersonEvent(event);
              }
            } else {
              console.log(`Ignoring Location WS Event: ${event.event.type}/${event.event.name}`);
            }
          }
        }
      );
    }
  }

  private handleAssetEvent(event: EventInstance): void {
    //Attempt to get the Asset the Event is for
    let datum: EventData = null;
    switch (event.event.name) {
      case EVENT_NAME_AssetEnteredLocation:
      case EVENT_NAME_AssetExitedLocation:
      case EVENT_NAME_AssetMovement: {
        datum = event.data.find(d => d.property.property == EVENT_PROP_Asset);
        break;
      }
      default: {
        datum = event.data.find(d => d.property.property == EVENT_PROP_Asset);
        if (datum) {
          console.log(`Ignoring Asset Event: ${event.event.name} for asset id ${datum.value}`);
        } else {
          console.log(`Ignoring Asset Event: ${event.event.name} for asset id of NULL`);
        }
        return;
      }
    }

    if (datum) {
      console.log("Received asset event " + event.event.name + " for asset id " + datum.value);
    }

    if (datum == null || datum.value == null) {
      console.log(`Ignoring Asset Event: ${event.event.name}, as no Asset data was found for it.`);
      return;
    } else {
      const assetId = Number.parseInt(datum.value, 10);
      let ui = this.mAssetsMap[assetId];

      switch (event.event.name) {
        case EVENT_NAME_AssetMovement:
        case EVENT_NAME_AssetEnteredLocation: {
          if (ui == null) {
            ui = new ObjectUi();
            this.mAssetsMap[assetId] = ui;


            this.mObjectService
              .getObjectInstanceForId(assetId)
              .then(
                response => {
                  ui.mObject = response;
                  ui.mObject.position = event.point;
                  this.drawObjectUi(ui);
                },
                error => this.handleError(error)
              );
          } else if (ui.mObject != null) {
            ui.mObject.position = event.point;
            this.drawObjectUi(ui);
            this.assetPositionChangedEvent.emit(ui.mObject);
          }
          break;
        }
        case EVENT_NAME_AssetExitedLocation: {
          this.eraseObjectUi(ui);
          delete this.mAssetsMap[assetId];
          break;
        }
      }
    }
  }

  private handlePersonEvent(event: EventInstance): void {
    //Attempt to get the Person the Event is for
    let datum: EventData = null;
    switch (event.event.name) {
      case EVENT_NAME_PersonEnteredLocation:
      case EVENT_NAME_PersonExitedLocation:
      case EVENT_NAME_PersonMovement: {
        datum = event.data.find(d => d.property.property == EVENT_PROP_Person);
        break;
      }
      default: {
        console.log(`Ignoring Person Event: ${event.event.name}`);
        return;
      }
    }

    if (datum == null || datum.value == null) {
      console.log(`Ignoring Person Event: ${event.event.name} as there was no Person for it.`);
    } else {
      const personId = Number.parseInt(datum.value, 10);
      let ui = this.mPeopleMap[personId];
      switch (event.event.name) {
        case EVENT_NAME_PersonEnteredLocation:
        case EVENT_NAME_PersonMovement: {
          if (ui == null) {
            ui = new PersonUI();
            this.mPeopleMap[personId] = ui;

            this.mObjectService
              .getObjectInstanceForId(personId)
              .then(
                response => {
                  ui.mPerson = response;
                  ui.mPerson.position = event.point;
                  this.drawPersonUi(ui);
                },
                error => this.handleError(error)
              );
          } else if (ui.mPerson != null) {
            ui.mPerson.position = event.point;
            this.drawPersonUi(ui);
          }
          break;


        }
        case EVENT_NAME_PersonExitedLocation: {
          this.erasePersonUi(ui);
          delete this.mPeopleMap[personId];
          break;
        }
      }
    }
  }


  private drawObjectUi(ui: ObjectUi): void {
    if (this._showAssets != true) {
      return;
    }

    if (ui == null) {
      //console.log("Not drawing object because ObjectUI is NULL")
    } else {
      if (ui.mObject == null) {
        //console.log("Not drawing object because object is NULL")
      } else {
        if (this.mLocationUi == null) {
          //console.log("Not drawing object because locationUi is NULL")
        } else {
          if (this.mLocationUi.mLocation == null) {
            //console.log("Not drawing object because location is NULL")
          } else {
            if (this.useVIP && this.assetsVIP[ui.mObject.id] == false) {
              //console.log("Not drawing object because object id " + ui.mObject.id + " is not in the VIP list of elements to be drawn");
            }
          }
        }
      }
    }
    if (ui != null && ui.mObject != null && this.mLocationUi != null && this.mLocationUi.mLocation != null ) {
      //console.warn("MIGHT show asset " + ui.mObject.id);
      if (!this.useVIP || (this.useVIP && this.assetsVIP[ui.mObject.id])) {
        const drawIcon = ui.mObject.locationInstance != null && ui.mObject.locationInstance.id == this.mLocationUi.mLocation.id && ui.mObject.position != null;

        if (ui.mIcon == null) {
          //console.log("Draw object " + ui.mObject.id + " (with new icon)");
          //Icon is currently not drawn...
          if (drawIcon) {
            //Icon is currently not drawn and it should be drawn... Draw/Add it to the Stage

            //Create the EventEmitter used to hide the Object's Popup when clicked
            this.mAssetHideEmitterMap[ui.mObject.id] = new EventEmitter<boolean>();

            //Draw the Object's Icon and it to the Stage
            const image = document.createElement('img');
            image.crossOrigin = 'Anonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
            image.src = this.getIconPathForObject(ui);
            ui.mIcon = new createjs.Bitmap(image);
            const component = this;

            //console.log("Grabbing new icon for object " + ui.mObject.id);
            ui.mIcon.image.onload = function (): void {
              //Position/Scale the Icon, then add it to the Stage so it will be drawn
              if (ui.mIcon == null) {
                console.error("Something went wrong loading icon for object " + ui.mObject.id + ". No data.");
                return;
              }
              const iconScale = component.getIconScale();
              ui.mIcon.scaleX = iconScale;
              ui.mIcon.scaleY = iconScale;
              ui.mIcon.x = component.translateMetersToPixels(ui.mObject.position.x) - (ui.mIcon.image.width * ui.mIcon.scaleX / 2);
              ui.mIcon.y = component.translateMetersToPixels(ui.mObject.position.y) - (ui.mIcon.image.height * ui.mIcon.scaleY / 2);
              ui.mLastPosition = new Point();
              ui.mLastPosition.x = ui.mObject.position.x;
              ui.mLastPosition.y = ui.mObject.position.y;
              component.mStage.addChild(ui.mIcon);

              // push this to the breadcrumbs
              const point = new Point();
              point.x = ui.mIcon.x;
              point.y = ui.mIcon.y;

              if (component._showBreadcrumbs) {
                const breadcrumb = new Breadcrumb();
                breadcrumb.icon = ui.mIcon.clone();
                breadcrumb.point = point;
                ui.breadcrumbs.push(breadcrumb);
                component.mStage.addChild(breadcrumb.icon);
              }

              //Once the Icon is added to the Stage, add a 'click' listener to handle displaying the popup
              ui.mIcon.on('click', function (event: createjs.MouseEvent): void {
                console.log(`Clicked on Asset: ${ui.mObject.id}/${ui.mObject.name} - Canceled? ${component.mCancelClickEvent}`);
                if (!component.mCancelClickEvent) {
                  //Hide the Popup if it is currently displayed (this resets the dragging info)
                  component.mAssetHideEmitterMap[ui.mObject.id].emit(true);


                  component.mObjectService.getObjectInstanceForId(ui.mObject.id).then(
                    asset => {
                      ui.mObject = asset;
                      ui.mObject.data.sort((a,b) => a.property.order - b.property.order);
                      //Show the popup at the mouse click
                      $("#" + component.getIdForObjectPopup(ui)).css('left', `${event.stageX}px`);
                      $("#" + component.getIdForObjectPopup(ui)).css('top', `${event.stageY}px`);
                      $("#" + component.getIdForObjectPopup(ui)).show();
                    },
                    error => {

                    }
                  );

                }
              });

              // Draw or remove the perimeter
              if (!component._showPerimeters && component.mPerimetersMap[ui.mObject.id]) {
                component.removePerimeter(ui.mObject.id);
              }

              if (component._showPerimeters && ui.mObject.perimeterRadius > 0) {
                component.drawPerimeter(ui.mObject.id);
              }

            }
          }
        } else {
          //Icon is currently drawn...
          if (drawIcon) {
            //Reposition/Rescale the Icon, animating any movement
            const iconScale = this.getIconScale();
            ui.mIcon.scaleX = iconScale;
            ui.mIcon.scaleY = iconScale;
            const x = this.translateMetersToPixels(ui.mObject.position.x) - (ui.mIcon.image.width * iconScale / 2);
            const y = this.translateMetersToPixels(ui.mObject.position.y) - (ui.mIcon.image.height * iconScale / 2);
            const differentPosition = !ui.mLastPosition || (ui.mObject.position.x != ui.mLastPosition.x) || (ui.mObject.position.y != ui.mLastPosition.y);
            ui.mLastPosition = new Point();
            ui.mLastPosition.x = ui.mObject.position.x;
            ui.mLastPosition.y = ui.mObject.position.y;
            if (this.shouldTween && ui.mIcon.x != 0 && ui.mIcon.y != 0) {
              createjs.Tween.get(ui.mIcon).to({x: x, y: y}, this.tweenDelay);
            } else {
              ui.mIcon.x = x;
              ui.mIcon.y = y;
            }

            // push this to the breadcrumbs
            const point = new Point();
            point.x = ui.mIcon.x;
            point.y = ui.mIcon.y;


            if (this._showBreadcrumbs && differentPosition) {
              const breadcrumb = new Breadcrumb();
              breadcrumb.icon = ui.mIcon.clone();
              breadcrumb.point = point;
              ui.breadcrumbs.push(breadcrumb);
              this.mStage.addChild(breadcrumb.icon);

              // Remove all elements greater than breadcrumbTrailSize
              if (ui.breadcrumbs.length > this.breadcrumbTrailSize) {
                ui.breadcrumbs.splice(0, ui.breadcrumbs.length - this.breadcrumbTrailSize);
              }
              // Dim all the other breadcrumbs
              // breadcrumbsLengthPadding is needed if there are less than breadcrumbTrailSize breadcrumbs on the screen
              const breadcrumbsLengthPadding = (this.breadcrumbTrailSize - ui.breadcrumbs.length) * 0.025;
              for (let i = 0; (i < ui.breadcrumbs.length); i++) {
                ui.breadcrumbs[i].icon.alpha = i * 0.025 + breadcrumbsLengthPadding;
              }
            }

            // Draw or remove the perimeter
            if (!this._showPerimeters && this.mPerimetersMap[ui.mObject.id]) {
              this.removePerimeter(ui.mObject.id);
            }

            if (this._showPerimeters && ui.mObject.perimeterRadius > 0) {
              this.drawPerimeter(ui.mObject.id);
            }

            // Move the perimeter
            if (this.mPerimetersMap[ui.mObject.id] && this._showPerimeters) {
              const perimX = this.translateMetersToPixels(ui.mObject.position.x);
              const perimY = this.translateMetersToPixels(ui.mObject.position.y);

              if (this.shouldTween) {
                createjs.Tween.get(this.mPerimetersMap[ui.mObject.id]).to({x: perimX, y: perimY}, this.tweenDelay);
              } else {
                this.mPerimetersMap[ui.mObject.id].x = perimX;
                this.mPerimetersMap[ui.mObject.id].y = perimY;
              }
            }

          } else {
            //Erase it from the Stage
            this.eraseObjectUi(ui);
          }
        }
      }
    }
  }

  private drawPerimeter(objectId: any): void {
    if (!this.mPerimetersMap[objectId]) {
      // Only draw the perimeter if it's not already drawn

      //console.log("draw perimeter: " + objectId);

      if (this.mAssetsMap[objectId]) {
        const radius = this.translateMetersToPixels(this.mAssetsMap[objectId].mObject.perimeterRadius);
        this.mPerimetersMap[objectId] = this.createPerimeter(this.mAssetsMap[objectId].mObject, radius);
      } else {
        const radius = this.translateMetersToPixels(this.mPeopleMap[objectId].mPerson.perimeterRadius);
        this.mPerimetersMap[objectId] = this.createPerimeter(this.mPeopleMap[objectId].mPerson, radius);
      }
      this.mStage.addChild(this.mPerimetersMap[objectId]);
    }
  }

  private removePerimeter(objectId: any): void {
    //console.log("Remove perimeter: " + objectId);
    this.mStage.removeChild(this.mPerimetersMap[objectId]);
    this.mPerimetersMap[objectId] = null;
  }

  private createPerimeter(objectInstance: ObjectInstance, radius: number): createjs.Shape {
    //console.log("Create perimeter: " + objectInstance.id);

    const shape = new createjs.Shape();
    shape.graphics.beginFill("yellow");
    shape.graphics.drawCircle(0, 0, radius);
    shape.graphics.endFill();
    shape.alpha = 0.15;
    shape.x = this.translateMetersToPixels(objectInstance.position.x);
    shape.y = this.translateMetersToPixels(objectInstance.position.y);
    return shape;
  }

  private eraseObjectUi(ui: ObjectUi): void {
    if (ui != null) {
      if (ui.mIcon != null) {
        //Hide the Object's Popup in case it is being shown...
        const hideEmitter = this.mAssetHideEmitterMap[ui.mObject.id];
        if (hideEmitter != null) {
          hideEmitter.emit(true);
          delete this.mAssetHideEmitterMap[ui.mObject.id];
        }

        //Remove the Object's Icon from the Stage
        this.mStage.removeChild(ui.mIcon);
        ui.mIcon = null;
      }

      // Remove the Asset's perimeter (if it exists) from the stage
      if (this.mPerimetersMap[ui.mObject.id]) {
        this.mStage.removeChild(this.mPerimetersMap[ui.mObject.id]);
        this.mPerimetersMap[ui.mObject.id] = null;
      }

    }
  }


  private drawPersonUi(ui: PersonUI): void {
    if (this._showPeople != true) {
     //console.log("Not drawing person as showPeople = false");
      return;
    }
    if (ui != null && ui.mPerson != null && this.mLocationUi != null && this.mLocationUi.mLocation != null) {
      if (!this.useVIP || (this.useVIP && this.peopleVIP[ui.mPerson.id])) {
        const drawIcon = ui.mPerson.locationInstance != null && ui.mPerson.locationInstance.id == this.mLocationUi.mLocation.id && ui.mPerson.position != null;

        if (ui.mIcon == null) {
          //Icon is currently not drawn...
          if (drawIcon) {
            //Icon is currently not drawn and it should be drawn... Draw/Add it to the Stage

            //Create the EventEmitter used to hide the Person's Popup when clicked
            this.mPeopleHideEmitterMap[ui.mPerson.id] = new EventEmitter<boolean>();

            //Draw the Person's Icon
            const image = document.createElement('img');
            image.crossOrigin = 'Anonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
            image.src = this.getIconPathForPerson(ui);
            ui.mIcon = new createjs.Bitmap(image);
            const component = this;
            ui.mIcon.image.onload = function (): void {
              if (ui.mIcon == null) {
                console.warn("No icon for ui element ");
                return;
              }
              //Position/Scale the Icon, then add it to the Stage so it will be drawn
              const iconScale = component.getIconScale();
              ui.mIcon.scaleX = iconScale;
              ui.mIcon.scaleY = iconScale;
              ui.mIcon.x = component.translateMetersToPixels(ui.mPerson.position.x) - (ui.mIcon.image.width * ui.mIcon.scaleX / 2);
              ui.mIcon.y = component.translateMetersToPixels(ui.mPerson.position.y) - (ui.mIcon.image.height * ui.mIcon.scaleY / 2);
              ui.mLastPosition = new Point();
              ui.mLastPosition.x = ui.mPerson.position.x;
              ui.mLastPosition.y = ui.mPerson.position.y;

              component.mStage.addChild(ui.mIcon);

              // push this to the breadcrumbs
              const point = new Point();
              point.x = ui.mIcon.x;
              point.y = ui.mIcon.y;

              if (component._showBreadcrumbs) {
                const breadcrumb = new Breadcrumb();
                breadcrumb.icon = ui.mIcon.clone();
                breadcrumb.point = point;
                ui.breadcrumbs.push(breadcrumb);
                component.mStage.addChild(breadcrumb.icon);
              }

              //Once the Icon added to the Stage, add a "click" listener to handle the popup
              ui.mIcon.on('click', function (event: createjs.MouseEvent): void {
                console.log(`Clicked on Person: ${ui.mPerson.id}/${ui.mPerson.name} - Canceled? ${component.mCancelClickEvent}`);
                if (!component.mCancelClickEvent) {
                  //Hide the Popup if it is currently displayed (this resets the dragging info)
                  component.mPeopleHideEmitterMap[ui.mPerson.id].emit(true);

                  //Show the popup at the mouse click
                  $("#" + component.getIdForPersonPopup(ui)).css('left', `${event.stageX}px`);
                  $("#" + component.getIdForPersonPopup(ui)).css('top', `${event.stageY}px`);
                  $("#" + component.getIdForPersonPopup(ui)).show();
                }
              });

              // Draw or remove the perimeter
              if (!component._showPerimeters && component.mPerimetersMap[ui.mPerson.id]) {
                component.removePerimeter(ui.mPerson.id);
              }

              if (component._showPerimeters && ui.mPerson.perimeterRadius > 0) {
                component.drawPerimeter(ui.mPerson.id);
              }
            }
          }
        } else {
          //Icon is currently drawn...
          if (drawIcon) {
            //Reposition/Rescale the Icon, animating any movement
            const iconScale = this.getIconScale();
            ui.mIcon.scaleX = iconScale;
            ui.mIcon.scaleY = iconScale;
            const x = this.translateMetersToPixels(ui.mPerson.position.x) - (ui.mIcon.image.width * iconScale / 2);
            const y = this.translateMetersToPixels(ui.mPerson.position.y) - (ui.mIcon.image.height * iconScale / 2);
            const differentPosition = !ui.mLastPosition || (ui.mPerson.position.x != ui.mLastPosition.x) || (ui.mPerson.position.y != ui.mLastPosition.y);
            ui.mLastPosition = new Point();
            ui.mLastPosition.x = ui.mPerson.position.x;
            ui.mLastPosition.y = ui.mPerson.position.y;
            if (this.shouldTween && ui.mIcon.x != 0 && ui.mIcon.y != 0) {
              createjs.Tween.get(ui.mIcon).to({x: x, y: y}, this.tweenDelay);
            } else {
              ui.mIcon.x = x;
              ui.mIcon.y = y;
            }

            // push this to the breadcrumbs
            const point = new Point();
            point.x = ui.mIcon.x;
            point.y = ui.mIcon.y;

            if (this._showBreadcrumbs && differentPosition) {
              const breadcrumb = new Breadcrumb();
              breadcrumb.icon = ui.mIcon.clone();
              breadcrumb.point = point;
              ui.breadcrumbs.push(breadcrumb);
              this.mStage.addChild(breadcrumb.icon);

              // Remove all elements greater than breadcrumbTrailSize
              if (ui.breadcrumbs.length > this.breadcrumbTrailSize) {
                ui.breadcrumbs.splice(0, ui.breadcrumbs.length - this.breadcrumbTrailSize);
              }
              // Dim all the other breadcrumbs
              // breadcrumbsLengthPadding is needed if there are less than breadcrumbTrailSize breadcrumbs on the screen
              const breadcrumbsLengthPadding = (this.breadcrumbTrailSize - ui.breadcrumbs.length) * 0.025;
              for (let i = 0; (i < ui.breadcrumbs.length); i++) {
                ui.breadcrumbs[i].icon.alpha = i * 0.025 + breadcrumbsLengthPadding;
              }
            }

            // Draw or remove the perimeter
            if (!this._showPerimeters && this.mPerimetersMap[ui.mPerson.id]) {
              this.removePerimeter(ui.mPerson.id);
            }

            if (this._showPerimeters && ui.mPerson.perimeterRadius > 0) {
              this.drawPerimeter(ui.mPerson.id);
            }

            // Move the perimeter
            if (this.mPerimetersMap[ui.mPerson.id]) {
              const perimX = this.translateMetersToPixels(ui.mPerson.position.x);
              const perimY = this.translateMetersToPixels(ui.mPerson.position.y);

              if (this.shouldTween) {
                createjs.Tween.get(this.mPerimetersMap[ui.mPerson.id]).to({x: perimX, y: perimY}, this.tweenDelay);
              } else {
                this.mPerimetersMap[ui.mPerson.id].x = perimX;
                this.mPerimetersMap[ui.mPerson.id].y = perimY;
              }
            }

          } else {
            //Erase it from the Stage
            this.erasePersonUi(ui);
          }
        }
      }
    }
  }

  private erasePersonUi(ui: PersonUI): void {
    //console.log("erasePersonUi called");
    if (ui != null) {
      if (ui.mIcon != null) {
        //Hide the Person's Popup in case it is being shown...
        const hideEmitter = this.mPeopleHideEmitterMap[ui.mPerson.id];
        if (hideEmitter != null) {
          hideEmitter.emit(true);
          delete this.mPeopleHideEmitterMap[ui.mPerson.id];
        }

        //Remove the Person's Icon from the Stage
        this.mStage.removeChild(ui.mIcon);
        ui.mIcon = null;
      }

      // Remove the Person's perimeter (if it exists) from the stage
      if (this.mPerimetersMap[ui.mPerson.id]) {
        this.mStage.removeChild(this.mPerimetersMap[ui.mPerson.id]);
        this.mPerimetersMap[ui.mPerson.id] = null;
      }

    }
  }


  private async drawEventPointUI(ui: EventPointUi): Promise<void> {
    //console.log("async drawEventPointUi() - Point: " + (ui == null ? "*NULL-UI*" : ui.mPoint == null ? "*NULL-POINT" : (ui.mPoint.x + "/" + ui.mPoint.y)));
    //Erase the old Event Point
    await this._eraseEventPointUi(this.mEventPointUi);

    //Set the Event Point being represented on the Map
    this.mEventPointUi = ui;

    //Draw the new Event Point
    await this._drawEventPointUi(ui);
  }

  private async _drawEventPointUi(ui: EventPointUi): Promise<void> {
    if (ui != null && ui.mPoint != null && ui.mPoint.x != null && ui.mPoint.y != null) {
      ui.mImage = new createjs.Bitmap('assets/images/googleMarker.png');
      const component = this;
      ui.mImage.image.onload = function(): void {
        component.redrawEventPointUi();
        component.mStage.addChild(ui.mImage);
      };
    }
  }

  private async _eraseEventPointUi(ui: EventPointUi): Promise<void> {
    if (ui != null && ui.mImage != null) {
      this.mStage.removeChild(ui.mImage);
      ui.mImage = null;
    }
  }

  private async redrawEventPointUi(): Promise<void> {
    if (this.mEventPointUi != null && this.mEventPointUi.mImage != null) {
      const ui = this.mEventPointUi;

      const iconScale = this.getIconScale();
      ui.mImage.scaleX = iconScale;
      ui.mImage.scaleY = iconScale;
      ui.mImage.x = this.translateMetersToPixels(ui.mPoint.x) - (ui.mImage.image.width * iconScale / 2);
      ui.mImage.y = this.translateMetersToPixels(ui.mPoint.y) - (ui.mImage.image.height * iconScale);
    }
  }

  getZoneParameters(zone: ZoneInstance): ZoneParameters {
    const zoneParameters = new ZoneParameters();

    const midPoint = new Point();
    const midPointName = new Point();

    // Find the coordinates of outside perimeterPoints
    let leftX = 10000;
    let rightX = 0;
    let topY = 10000;
    let bottomY = 0;

    for(const perimeterPoint of zone.perimeterPoints) {
      if (perimeterPoint.x < leftX) {
        leftX = perimeterPoint.x;
      }

      if (perimeterPoint.x > rightX) {
        rightX = perimeterPoint.x;
      }

      if (perimeterPoint.y < topY) {
        topY = perimeterPoint.y;
      }

      if (perimeterPoint.y > bottomY) {
        bottomY = perimeterPoint.y;
      }
    }

    midPoint.x = leftX + ((rightX - leftX) / 2);
    midPoint.y = topY + ((bottomY - topY) / 2);

    midPointName.x = leftX + ((rightX - leftX) / 4);
    midPointName.y = topY + ((bottomY - topY) / 2);

    //console.log("getZoneParameters():  leftX=" + leftX + ",rightX= " + rightX + ", topY = " +
    //  topY + ",bottomY=" + bottomY + ", midX= " + midPoint.x + ", midY= " + midPoint.y);

    zoneParameters.midPoint = midPoint;
    zoneParameters.midPointPixels = new Point();

    zoneParameters.midPointPixels.x = this.translateMetersToPixels(zoneParameters.midPoint.x);
    zoneParameters.midPointPixels.y = this.translateMetersToPixels(zoneParameters.midPoint.y);
    //zoneParameters.midPointPixels.x = zoneParameters.midPoint.x / (this.mStageScaleIndex * SCALE_INDEX_MULTIPLIER);
    //zoneParameters.midPointPixels.y = zoneParameters.midPoint.y / (this.mStageScaleIndex * SCALE_INDEX_MULTIPLIER);

    zoneParameters.midPointName = midPointName;
    zoneParameters.width = rightX - leftX;
    zoneParameters.height = bottomY - topY;

    return zoneParameters;
  }

  getSublocationParameters(zone: LocationInstance): ZoneParameters {

    const sublocationParameters = new ZoneParameters();

    const midPoint = new Point();
    const midPointName = new Point();

    // Find the coordinates of outside perimeterPoints
    let leftX = 10000;
    let rightX = 0;
    let topY = 10000;
    let bottomY = 0;

    for(const perimeterPoint of zone.perimeterPoints) {
      if (perimeterPoint.x < leftX) {
        leftX = perimeterPoint.x;
      }

      if (perimeterPoint.x > rightX) {
        rightX = perimeterPoint.x;
      }

      if (perimeterPoint.y < topY) {
        topY = perimeterPoint.y;
      }

      if (perimeterPoint.y > bottomY) {
        bottomY = perimeterPoint.y;
      }
    }

    midPoint.x = leftX + ((rightX - leftX) / 2);
    midPoint.y = topY + ((bottomY - topY) / 2);

    midPointName.x = leftX + ((rightX - leftX) / 4);
    midPointName.y = topY + ((bottomY - topY) / 2);

    //console.log("getZoneParameters():  leftX=" + leftX + ",rightX= " + rightX + ", topY = " +
    //  topY + ",bottomY=" + bottomY + ", midX= " + midPoint.x + ", midY= " + midPoint.y);

    sublocationParameters.midPoint = midPoint;
    sublocationParameters.midPointPixels = new Point();

    sublocationParameters.midPointPixels.x = this.translateMetersToPixels(sublocationParameters.midPoint.x);
    sublocationParameters.midPointPixels.y = this.translateMetersToPixels(sublocationParameters.midPoint.y);
    //zoneParameters.midPointPixels.x = zoneParameters.midPoint.x / (this.mStageScaleIndex * SCALE_INDEX_MULTIPLIER);
    //zoneParameters.midPointPixels.y = zoneParameters.midPoint.y / (this.mStageScaleIndex * SCALE_INDEX_MULTIPLIER);

    sublocationParameters.midPointName = midPointName;
    sublocationParameters.width = rightX - leftX;
    sublocationParameters.height = bottomY - topY;

    return sublocationParameters;
  }


  reduceImageWidthAndHeightByCanvas(imageWidth: number, imageHeight: number): Point {
    //console.log("reduceImageWidthAndHeightByCanvas(" + imageWidth + "," + imageHeight + ")");

    const point = new Point();
    point.x = this.mCanvasElement.width;
    point.y = this.mCanvasElement.height;

    // If the width or height are larger than the canvas, reduce the width or height to the canvas size
    if (imageWidth > this.mCanvasElement.width) {
      point.x = this.mCanvasElement.width;
    }
    if (imageHeight > this.mCanvasElement.height) {
      point.y = this.mCanvasElement.height;
    }

    // If the canvas is moved over (there is whitespace left or top), recalculate the width and height displayed
    if (imageWidth + this.mStage.x > this.mCanvasElement.width) {
      point.x = this.mCanvasElement.width - this.mStage.x;
    }

    if (imageHeight + this.mStage.y > this.mCanvasElement.height) {
      point.y = this.mCanvasElement.height - this.mStage.y;
    }

    return point;
  }

  executeZoomToZone(zone: ZoneInstance): void {
    console.log("executeZoomToZone(" + zone.id + ")");

    // Find the coordinates of outside perimeterPoints
    const zoneParameters = this.getZoneParameters(zone);

    const currentScale = this.mStageScaleIndex * SCALE_INDEX_MULTIPLIER;
    //console.log("CURRENT SCALE=" + currentScale + ", LOCATIONUI.mLocation.scale = " + this.mLocationUi.mLocation.scale);


    const zoneMidpointPixelsX = zoneParameters.midPointPixels.x * currentScale;
    const zoneMidpointPixelsY = zoneParameters.midPointPixels.y * currentScale;

    //console.log("zoneMidpointPixelsX=" + zoneMidpointPixelsX + ", zoneMidpointPixelsY=" + zoneMidpointPixelsY);

    // ***********************************************
    // Calculate imageWidthCurrent and imageHeightCurrent
    // ***********************************************

    // First calculate the totalWidth and totalHeight in the current scale step
    const imageWidth = this.mLocationUi.mImage.image.width;
    const imageHeight = this.mLocationUi.mImage.image.height;
    //console.log("imageWidth= " + imageWidth + ", imageHeight=" + imageHeight);

    const imageWidthCurrent = imageWidth * currentScale;
    const imageHeightCurrent = imageHeight * currentScale;

    const currentReduction = this.reduceImageWidthAndHeightByCanvas(imageWidthCurrent, imageHeightCurrent);
    const imageWidthCurrentDisplayed = currentReduction.x;
    const imageHeightCurrentDisplayed = currentReduction.y;

    //console.log("imageWidthCurrentDisplayed=" + imageWidthCurrentDisplayed + ", imageHeightCurrentDisplayed=" + imageHeightCurrentDisplayed);

    // Figure out the left X pixel shown, and the right X pixel shown
    const stageLeftX = (this.mStage.x > 0) ? this.mStage.x : 0;
    const stageRightX = this.mCanvasElement.width - (this.mStage.x + imageWidthCurrentDisplayed);

    // Figure out the top Y pixel shown, and the bottom Y pixel shown
    const stageTopY = (this.mStage.y > 0) ? this.mStage.y : 0;
    const stageBottomY = this.mCanvasElement.height - (this.mStage.y + imageHeightCurrentDisplayed);

    //console.log("stageLeftX=" + stageLeftX + ", stageRightX=" + stageRightX);
    //console.log("stageTopY=" + stageTopY + ", stageBottomY=" + stageBottomY);

    // Find pixel midpoint of canvas
    const pixelsMidpointX = (this.mCanvasElement.width / 2) - this.mStage.x;
    const pixelsMidpointY = (this.mCanvasElement.height / 2) - this.mStage.y;

    //console.log("pixelsMidpointX=" + pixelsMidpointX + ", pixelsMidpointY=" + pixelsMidpointY);

    const diffMidpointX = pixelsMidpointX - zoneMidpointPixelsX;
    const diffMidpointY = pixelsMidpointY - zoneMidpointPixelsY;

    //console.log("diffMidpointX=" + diffMidpointX + ", diffMidpointY=" + diffMidpointY);

    const zoneMidpointAtCurrentScale = (zoneMidpointPixelsX / currentScale);
    //console.log("**** " + zoneMidpointAtCurrentScale + " / " + imageWidth);

    const diffMidpointXPercentage = (zoneMidpointPixelsX / currentScale) / imageWidth;
    const diffMidpointYPercentage = (zoneMidpointPixelsY / currentScale) / imageHeight;

    //const diffMidpointXPercentage = diffMidpointX / imageWidthCurrent;
    //const diffMidpointYPercentage = diffMidpointY / imageHeightCurrent;

    //console.log("diffMidpointXPercentage= " + diffMidpointXPercentage + ", diffMidpointYPercentage= " + diffMidpointYPercentage);

    // Figure out how much scaling will be needed to get this zone on 50% of the screen in width
    let zoneWidthPixels = this.translateMetersToPixels(zoneParameters.width) * currentScale;
    let zoneHeightPixels = this.translateMetersToPixels(zoneParameters.height) * currentScale;

    //console.log("ZoneWidthInPixels=" + zoneWidthPixels);

    let scaleIncrement: number;
    scaleIncrement = 0;

    //console.log("zoneWidthPixels * ZOOM_ZONE_LEVEL = " + (zoneWidthPixels * ZOOM_ZONE_LEVEL) + ", imageWidthCurrentDisplayed=" + imageWidthCurrentDisplayed);

    if (zoneWidthPixels * ZOOM_ZONE_LEVEL < imageWidthCurrentDisplayed && zoneHeightPixels * ZOOM_ZONE_LEVEL < imageHeightCurrentDisplayed) {
      // Zoom In
      //console.log("*** ZOOM IN ***");
      while (zoneWidthPixels * ZOOM_ZONE_LEVEL < this.mCanvasElement.width && zoneHeightPixels * ZOOM_ZONE_LEVEL < this.mCanvasElement.height) {
        scaleIncrement++;
        const scaleMultiplier = (this.mStageScaleIndex + scaleIncrement) * SCALE_INDEX_MULTIPLIER;
        zoneWidthPixels = this.translateMetersToPixels(zoneParameters.width) * scaleMultiplier;
        zoneHeightPixels = this.translateMetersToPixels(zoneParameters.height) * scaleMultiplier;
      }

    } else {
      const zoneWidthNow = zoneWidthPixels * ZOOM_ZONE_LEVEL;
      const zoneHeightNow = zoneHeightPixels * ZOOM_ZONE_LEVEL;

      if (zoneWidthNow > this.mCanvasElement.width  && zoneHeightPixels * ZOOM_ZONE_LEVEL > this.mCanvasElement.height) {
        // Zoom Out
        //console.log("*** ZOOM OUT ***");


        //console.log("Comparing zoneWidthNow=" + zoneWidthNow + " with canvasWidth=" + this.mCanvasElement.width);
        //console.log("Comparing zoneHeightNow=" + zoneHeightNow + " with canvasHeight=" + this.mCanvasElement.height);
        while (zoneWidthNow > this.mCanvasElement.width && zoneHeightPixels * ZOOM_ZONE_LEVEL > this.mCanvasElement.height) {
          scaleIncrement--;
          const scaleMultiplier = (this.mStageScaleIndex + scaleIncrement) * SCALE_INDEX_MULTIPLIER;
          zoneWidthPixels = this.translateMetersToPixels(zoneParameters.width) * scaleMultiplier;
          zoneHeightPixels = this.translateMetersToPixels(zoneParameters.height) * scaleMultiplier;
        }
      } else {
        //console.log("*** NO ZOOM ***");
      }
    }

    //console.log("scaleIncrement = " + scaleIncrement);

    // Calculate the zoomed parameters
    const zoomedScale = (this.mStageScaleIndex + scaleIncrement) * SCALE_INDEX_MULTIPLIER;
    const imageWidthZoomed = imageWidth * zoomedScale;
    const imageHeightZoomed = imageHeight * zoomedScale;

    const zoomedReduction = this.reduceImageWidthAndHeightByCanvas(imageWidthZoomed, imageHeightZoomed);
    const imageWidthZoomedDisplayed = zoomedReduction.x;
    const imageHeightZoomedDisplayed = zoomedReduction.y;




    const that = this;
    //console.log("FIRST TWEEN TO: x=" + (this.mStage.x) + " + " + diffMidpointX + ",y=" + (this.mStage.y) + " + " + diffMidpointY);
    const tween = createjs.Tween.get(this.mStage).to({ x: this.mStage.x + diffMidpointX, y: this.mStage.y + diffMidpointY}, this.tweenDelay)
      .call(function(tweenObject: Tween): void {
        //console.log("Tween done");

        // Find pixel midpoint of canvas
        const zoneMidpointPixelsZoomedX = zoneParameters.midPointPixels.x * zoomedScale;
        const zoneMidpointPixelsZoomedY = zoneParameters.midPointPixels.y * zoomedScale;

        const pixelsMidpointZoomedX = (that.mCanvasElement.width / 2) - that.mStage.x;
        const pixelsMidpointZoomedY = (that.mCanvasElement.height / 2) - that.mStage.y;

        //console.log("pixelsMidpointX=" + pixelsMidpointX + ", pixelsMidpointY=" + pixelsMidpointY);

        const diffMidpointZoomedX = pixelsMidpointZoomedX - zoneMidpointPixelsZoomedX;
        const diffMidpointZoomedY = pixelsMidpointZoomedY - zoneMidpointPixelsZoomedY;

        const xMoveTo = that.mStage.x + diffMidpointZoomedX;
        const yMoveTo = that.mStage.y + diffMidpointZoomedY;

        createjs.Tween.get(that.mStage).to({
          x: xMoveTo, y: yMoveTo,
          scaleX: (that.mStageScaleIndex + scaleIncrement) * SCALE_INDEX_MULTIPLIER,
          scaleY: (that.mStageScaleIndex + scaleIncrement) * SCALE_INDEX_MULTIPLIER
        }, that.tweenDelay)
          .call(function (tweenObject2: Tween): void {

              that.mStageScaleIndex = that.mStageScaleIndex + scaleIncrement;

            // Redraw Zone names
            if (that._showZoneNames) {
              that.removeZoneNamesFromDisplay();
              that.addZoneNamesToDisplay();
            }


            //If any People are displayed... They need to be redrawn so that their Icon appears the same size no matter the scale
            Object.keys(that.mPeopleMap).forEach(key => {
              const ui = that.mPeopleMap[key];
              if (ui != null) {
                that.erasePersonUi(ui);
                that.drawPersonUi(ui);
              }
            });

            //If any Assets are displayed... They need to be redrawn so that their Icon appears the same size no matter the scale
            Object.keys(that.mAssetsMap).forEach(key => {
              const ui = that.mAssetsMap[key];
              if (ui != null) {
                that.eraseObjectUi(ui);
                that.drawObjectUi(ui);
              }
            });

            //Redraw the event point so that it appears the same size no matter the scale
            that.redrawEventPointUi();

          });

      });
  }

  getCenterPointOfCanvas(): Point {
    const point = new Point();

    // Find out the size of the image in the current scaling
    const currentScale = this.mStageScaleIndex * SCALE_INDEX_MULTIPLIER;
    const imageWidth = this.mLocationUi.mImage.image.width;
    const imageHeight = this.mLocationUi.mImage.image.height;
    const imageWidthCurrent = imageWidth * currentScale;
    const imageHeightCurrent = imageHeight * currentScale;

    // Find pixel midpoint of canvas
    const pixelsMidpointX = (this.mCanvasElement.width / 2) - (imageWidthCurrent / 2) + this.mStage.x;
    const pixelsMidpointY = (this.mCanvasElement.height / 2) - (imageHeightCurrent / 2) + this.mStage.y;

    point.x = pixelsMidpointX;
    point.y = pixelsMidpointY;

    return point;
  }

  // zoom To Original returns the page to it's original zooming
  zoomToOriginal(): void {
    if (this.mLocationUi && this.mLocationUi.mImage) {
      const originalShouldTween = this.shouldTween;
      this.shouldTween = false;

      // TODO: Animate this

      // Set the scale back to original scaling
      const horizontalScale = this.mCanvasElement.offsetWidth / this.mLocationUi.mImage.image.width;
      const verticalScale = (this.mCanvasElement.offsetHeight - VERTICAL_PADDING) / this.mLocationUi.mImage.image.height;
      const scaleIndex = Math.floor((horizontalScale < verticalScale ? horizontalScale : verticalScale) / SCALE_INDEX_MULTIPLIER);
      this.setScaleIndex(scaleIndex);

      // Move the coordinates back to centered
      const diffX = this.mCanvasElement.offsetWidth - (this.mStage.scaleX * this.mLocationUi.mImage.image.width);
      const diffY = this.mCanvasElement.offsetHeight - VERTICAL_PADDING - (this.mStage.scaleY * this.mLocationUi.mImage.image.height);
      this.mStage.x = diffX / 2;
      this.mStage.y = diffY / 2;

      this.shouldTween = originalShouldTween;

      this.mappingEvent.emit(MappingEvent.ZOOM_TO_ORIGINAL);

    }

  }


  // zoomIn includes setting scale and moving canvas so we zoom on centered pixel
  zoomIn(amount?: number): void {
    const originalShouldTween = this.shouldTween;
    this.shouldTween = false;
    amount = (!amount) ? 1 : Math.ceil(amount);

    console.log("zoomIn(" + amount + ")");

    const scaleIndexAfterZoom = MathModule.clamp(this.mStageScaleIndex +amount, MIN_SCALE_INDEX, MAX_SCALE_INDEX);
    if (scaleIndexAfterZoom == this.mStageScaleIndex) {
      console.log("Reached maximum zoom in.");
      return;
    }

    const centerPoint = this.getCenterPointOfCanvas();
    const originalX = this.mStage.x + this.mLocationUi.mImage.image.width * this.mStageScaleIndex * SCALE_INDEX_MULTIPLIER / 2;
    const originalY = this.mStage.y + this.mLocationUi.mImage.image.height * this.mStageScaleIndex * SCALE_INDEX_MULTIPLIER / 2;
    const newX = this.mStage.x + this.mLocationUi.mImage.image.width * (scaleIndexAfterZoom) * SCALE_INDEX_MULTIPLIER / 2;
    const newY = this.mStage.y + this.mLocationUi.mImage.image.height * (scaleIndexAfterZoom) * SCALE_INDEX_MULTIPLIER / 2;
    const originalStageX = this.mStage.x;
    const originalStageY = this.mStage.y;

    this.setScaleIndex(scaleIndexAfterZoom);
    const diffX = newX - originalX;
    const diffY = newY - originalY;

    const centerPoint2 = this.getCenterPointOfCanvas();

    // Find pixel midpoint of canvas
    const centerPoint2X = (this.mCanvasElement.width / 2 )- this.mStage.x - diffX;
    const centerPoint2Y = (this.mCanvasElement.height / 2) - this.mStage.y - diffY;


    // Move this by the difference in the original centered point
    const moveX = centerPoint2.x - centerPoint.x;
    const moveY = centerPoint2.y - centerPoint.y;

    //console.log("MOVE_STAGE from zoomIn");
    this.moveStage(moveX, moveY);

    this.shouldTween = originalShouldTween;

    this.mappingEvent.emit(MappingEvent.ZOOM_IN);

  }

  // zoomOut includes setting scale and moving canvas so we zoom on center
  zoomOut(amount?: number): void {
    const originalShouldTween = this.shouldTween;
    this.shouldTween = false;

    amount = (!amount) ? 1 : Math.ceil(amount);

    console.log("zoomOut(" + amount + ")");

    const scaleIndexAfterZoom = MathModule.clamp(this.mStageScaleIndex - amount, MIN_SCALE_INDEX, MAX_SCALE_INDEX);
    if (scaleIndexAfterZoom == this.mStageScaleIndex) {
      console.log("Reached maximum zoom out.");
      return;
    }

    //console.log("image Width=" + this.mLocationUi.mImage.image.width + ", image Height=" + this.mLocationUi.mImage.image.height + ",scaling=" + this.mStageScaleIndex);

    const originalX = this.mStage.x + this.mLocationUi.mImage.image.width * this.mStageScaleIndex * SCALE_INDEX_MULTIPLIER / 2;
    const originalY = this.mStage.y + this.mLocationUi.mImage.image.height * this.mStageScaleIndex * SCALE_INDEX_MULTIPLIER / 2;

    //console.log("originalX=" + originalX + ", originalY=" + originalY);


    const newX = this.mStage.x + this.mLocationUi.mImage.image.width * (scaleIndexAfterZoom) * SCALE_INDEX_MULTIPLIER / 2;
    const newY = this.mStage.y + this.mLocationUi.mImage.image.height * (scaleIndexAfterZoom) * SCALE_INDEX_MULTIPLIER / 2;

    const diffX = newX - originalX;
    const diffY = newY - originalY;

    if (this.mCurrentPos) {
      //console.log("currentPos=" + this.mCurrentPos.x + "," + this.mCurrentPos.y);
    }
    //console.log("currentPosLocImage=" + this.mStage.x + "," + this.mStage.y);

    this.setScaleIndex(scaleIndexAfterZoom);

    //console.log("MOVE_STAGE from zoomOut");
    this.moveStage(-diffX, -diffY);

    this.shouldTween = originalShouldTween;

    this.mappingEvent.emit(MappingEvent.ZOOM_OUT);

  }

  setScaleIndex(scaleIndex: number, setInitial?: boolean): void {
    //Correct any invalid scale indexes
    scaleIndex = MathModule.clamp(scaleIndex, MIN_SCALE_INDEX, MAX_SCALE_INDEX);

    //Set the scale index, only updating if it is actually changing...
    if (this.mStageScaleIndex != scaleIndex) {
      console.log("Changing Scale Index: " + this.mStageScaleIndex + " => " + scaleIndex);
      this.mStageScaleIndex = scaleIndex;
      const newScale = scaleIndex * SCALE_INDEX_MULTIPLIER;
      this.mStage.scaleX = newScale;
      this.mStage.scaleY = newScale;

      if (setInitial) {
        this.initialScale = newScale;
      }

      //If the Scale is changing and an Event Point is drawn... Redraw it so that it appears the same size no matter the scale
      this.redrawEventPointUi();
    }

    //If any People are displayed... They need to be redrawn so that their Icon appears the same size no matter the scale
    Object.keys(this.mPeopleMap).forEach(key => {
      const ui = this.mPeopleMap[key];
      if (ui != null) {
        this.drawPersonUi(ui);
      }
    });

    //If any Assets are displayed... They need to be redrawn so that their Icon appears the same size no matter the scale
    Object.keys(this.mAssetsMap).forEach(key => {
      const ui = this.mAssetsMap[key];
      if (ui != null) {
        this.drawObjectUi(ui);
      }
    });


    // Redraw Zone names
    if (this._showZoneNames) {
      this.removeZoneNamesFromDisplay();
      this.addZoneNamesToDisplay();
    }

  }

  private getIconScale(): number {
    return (1 / this.mStage.scaleX);
  }

  private translateMetersToPixels(posVal: number): number {
    if (this.mLocationUi != null && this.mLocationUi.mLocation != null && this.mLocationUi.mLocation.scale != null) {
      return posVal * this.mLocationUi.mLocation.scale;
    } else {
      return posVal;
    }
  }

  private translatePixelsToMeters(pixelVal: number): number {
    if (this.mLocationUi != null && this.mLocationUi.mLocation != null && this.mLocationUi.mLocation.scale != null) {
      return pixelVal / this.mLocationUi.mLocation.scale;
    } else {
      return pixelVal;
    }
  }


  private getIconPathForObject(ui: ObjectUi): string {
    return this.mImageService.getIconSourceForObjectInstance(ui.mObject);
  }

  private getIconPathForPerson(ui: PersonUI): string {
    return this.mImageService.getIconSourceForObjectInstance(ui.mPerson);
  }

  getIdForPersonPopup(ui: PersonUI): string {
    return `personPopupDialog_${ui.mPerson.id}`;
  }

  getIdForObjectPopup(ui: ObjectUi): string {
    return `objectPopupDialog_${ui.mObject.id}`;
  }


  /****************************
   * Functions to update the UI manually
   */

  turnTrackingOff(): void {
    this.trackPeopleAndAssets = false;
  }

  turnTrackingOn(): void {
    this.trackPeopleAndAssets = true;

    if (1==1) {

    }
  }

  updateWithMovementEvent(eventType: string, eventId: number, point: Point, locationInstance?: LocationInstance): void {
    switch(eventType) {
      case "ASSET":
        if (this.mPeopleMap[eventId]) {
          console.warn("Asset with same ID (" + eventId + ") as Person sent. Treating as Person instead.");
          this.updateWithPersonEvent(eventId, EVENT_NAME_PersonMovement, point, locationInstance);
        } else {
          this.updateWithAssetEvent(eventId, EVENT_NAME_AssetMovement, point, locationInstance);
        }
        break;
      case "PEOPLE":
        if (this.mAssetsMap[eventId]) {
          console.warn("Person with same ID (" + eventId + ") as Asset sent. Treating as Asset instead.");
          this.updateWithAssetEvent(eventId, EVENT_NAME_AssetMovement, point, locationInstance);
        } else {
          this.updateWithPersonEvent(eventId, EVENT_NAME_PersonMovement, point, locationInstance);
        }
        break;
    }
  }

  updateWithPersonEvent(personId: number, eventName: string, point: Point, locationInstance?: LocationInstance): void {
    let ui = this.mPeopleMap[personId];
    switch (eventName) {
      case EVENT_NAME_PersonMovement:
      case EVENT_NAME_PersonEnteredLocation: {
        if (ui == null) {
          //console.log("######################## Grabbing Person for id=" + personId);
          ui = new PersonUI();

          this.mPeopleMap[personId] = ui;

          this.mObjectService
            .getObjectInstanceForId(personId)
            .then(
              response => {
                if (response == null) {
                  console.warn("updateWithPersonEvent: getObjectInstanceForId(" + personId + ") is returning NULL");
                } else {
                  ui.mPerson = response;
                  ui.mPerson.position = point;

                  if (locationInstance) {
                    ui.mPerson.locationInstance = locationInstance;
                  }

                  this.drawPersonUi(ui);
                }
              },
              error => this.handleError(error)
            );
        } else if (ui.mPerson != null) {
          ui.mPerson.position = point;
          this.drawPersonUi(ui);
        }
        break;
      }
      case EVENT_NAME_PersonExitedLocation: {
        this.erasePersonUi(ui);
        delete this.mPeopleMap[personId];
        break;
      }
    }
  }

  updateWithAssetEvent(assetInstanceId: number, eventName: string, point: Point, locationInstance?: LocationInstance): void {
    let ui = this.mAssetsMap[assetInstanceId];
    switch (eventName) {
      case EVENT_NAME_AssetMovement:
      case EVENT_NAME_AssetEnteredLocation: {
        if (ui == null) {
          ui = new ObjectUi();

          this.mAssetsMap[assetInstanceId] = ui;

          this.mObjectService
            .getObjectInstanceForId(assetInstanceId)
            .then(
              response => {
                if (response == null) {
                  console.warn("updateWithAssetEvent: getObjectInstanceForId(" + assetInstanceId + ") is returning NULL");
                } else {
                  ui.mObject = response;
                  ui.mObject.position = point;

                  if (locationInstance) {
                    ui.mObject.locationInstance = locationInstance;
                  }

                  this.drawObjectUi(ui);
                }
              },
              error => this.handleError(error)
            );
        } else if (ui.mObject != null) {
          ui.mObject.position = point;
          this.drawObjectUi(ui);
        }
        break;
      }
      case EVENT_NAME_AssetExitedLocation: {
        this.eraseObjectUi(ui);
        delete this.mAssetsMap[assetInstanceId];
        break;
      }
    }
  }

  async refreshAssetsInLocation(): Promise<void> {
    //console.log("Refreshing all Assets in Location...");

    let assets;
    try {
      assets = await this.mObjectService.getAllAssetObjectInstancesPaged(0, this.resultsPerPageValues, "ASC", "");
    } catch (error) {
      this.handleError(error);
    }

    if (assets != null) {
      for (const asset of assets.data) {
        // If this asset is in this location
        if (asset.locationInstance != null && this._locationId == asset.locationInstance.id) {
          if (this.mAssetsMap[asset.id] == null) {
            const ui = new ObjectUi();
            ui.mObject = asset;
            ui.mObject.data.sort((a, b) => a.property.order - b.property.order );
            this.mAssetsMap[asset.id] = ui;
            this.drawObjectUi(ui);
          } else {
            const ui = this.mAssetsMap[asset.id];
            ui.mObject = asset;
            ui.mObject.data.sort((a, b) => a.property.order - b.property.order );
            this.drawObjectUi(ui);
          }
        }
      }
    }
  }

  async refreshPeopleInLocation(): Promise<void> {
    //console.log("Refreshing all People in Location...");

    let people;
    try {
      people = await this.mObjectService.getAllPeopleObjectInstancesPaged(0, this.resultsPerPageValues, "ASC", "");
      //console.log("Received all people object Instances");
    } catch(error) {
      this.handleError(error);
    }

    if (people != null) {
      for (const person of people.data) {
        // If this asset is in this location
        if (person.locationInstance != null && this._locationId == person.locationInstance.id) {
          if (this.mPeopleMap[person.id] == null) {
            const ui = new PersonUI();
            ui.mPerson = person;
            ui.mPerson.data.sort((a, b) => a.property.order - b.property.order );
            this.mPeopleMap[person.id] = ui;
            this.drawPersonUi(ui);
          } else {
            const ui = this.mPeopleMap[person.id];
            ui.mPerson = person;
            ui.mPerson.data.sort((a, b) => a.property.order - b.property.order );
            this.drawPersonUi(ui);
          }
        }
      }
    }
  }

  activateOrDeactivateBreadcrumbs(): void {
    // If breadcrumbs are actived, they will show on the next movement
    // If breadcrumbs are deactived, we need to remove them from the GUI
    if (!this.showBreadcrumbs) {
      this.removeBreadcrumbs();
    }
  }

  activateOrDeactivatePerimeters(): void {
    // Perimeters are drawn depending on the _showPerimeters variable.
    // Simply redrawing everything will show/remove the perimeters properly.
    if (this.mStage) {
      if (this._showPerimeters) {
        // Add all the perimeters when we choose to show them
        for (const assetId of Object.keys(this.mAssetsMap)) {
          const ui: ObjectUi = this.mAssetsMap[assetId];
          if (ui && ui.mObject.perimeterRadius > 0) {
            this.drawPerimeter(assetId);
          }
        }
        for (const personId of Object.keys(this.mPeopleMap)) {
          const ui: PersonUI = this.mPeopleMap[personId];
          if (ui && ui.mPerson.perimeterRadius > 0) {
            this.drawPerimeter(personId);
          }
        }
      } else {
        // Remove all the perimeters when we choose to not show them
        for(const perimeter of Object.keys(this.mPerimetersMap)) {
          this.removePerimeter(perimeter);
        }

      }
    }

  }

  removeBreadcrumbs(): void {
    // Remove all the breadcrumbs when we choose to not show them
    for(const person of Object.keys(this.mPeopleMap)) {
      for(const breadcrumb of this.mPeopleMap[person].breadcrumbs) {
        this.mStage.removeChild(breadcrumb.icon);
      }
      this.mPeopleMap[person].breadcrumbs = [];
    }
  }

  removeAllPeopleAndAssets(): void {
    if (this.mStage) {
      //console.log("removing all people and assets");
      this.mStage.removeAllChildren();
      this.mPeopleMap = {};
      this.mAssetsMap = {};
      this.mPerimetersMap = {};
    }
  }

  removeAllPeople(): void {
    // Remove all the people when we choose to not show them
    for(const personId of Object.keys(this.mPeopleMap)) {
      this.mStage.removeChild(this.mPeopleMap[personId].mIcon);
      delete this.mPeopleMap[personId].mIcon;

      // Remove all breadcrumbs of this person
      for(const breadcrumb of this.mPeopleMap[personId].breadcrumbs) {
        this.mStage.removeChild(breadcrumb.icon);
      }
      this.mPeopleMap[personId].breadcrumbs = [];

      // Remove all perimeters of this person
      if (this.mPerimetersMap[personId]) {
        this.mStage.removeChild(this.mPerimetersMap[personId]);
        this.mPerimetersMap[personId] = null;
      }

    }
  }

  removeAllAssets(): void {
    // Remove all the assets when we choose to not show them
    for(const assetId of Object.keys(this.mAssetsMap)) {
      this.mStage.removeChild(this.mAssetsMap[assetId].mIcon);
      delete this.mAssetsMap[assetId].mIcon;

      // Remove all breadcrumbs of this asset
      for(const breadcrumb of this.mAssetsMap[assetId].breadcrumbs) {
        this.mStage.removeChild(breadcrumb.icon);
      }
      this.mAssetsMap[assetId].breadcrumbs = [];

      // Remove all perimeters of this asset
      if (this.mPerimetersMap[assetId]) {
        this.mStage.removeChild(this.mPerimetersMap[assetId]);
        this.mPerimetersMap[assetId] = null;
      }

    }
  }


  activateTweening(tween: boolean): void {
    this.shouldTween = tween;
  }

  setTweenDelay(tweenDelay: number): void {
    this.tweenDelay = tweenDelay;
  }

  getMapCanvasId(): string {
    if (this.mapCanvasId == null) {
      this.mapCanvasId = "mapCanvas" + MapComponent.mapComponentCounter++;
    }

    return this.mapCanvasId;
  }

  private handleError(error: any): void {
    console.error("handleError() - " + error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
    }
  }

}

class ZoneUi {
  mZone: ZoneInstance;
  mShape: createjs.Shape;
  mText: createjs.Text;
}

class SublocationUi {
  mSublocation: LocationInstance;
  mShape: createjs.Shape;
  mText: createjs.Text;
}


class ZoneParameters {
  midPoint: Point;
  midPointPixels: Point;
  midPointName: Point;
  width: number;
  widthPixels: number;
  height: number;
  heightPixels: number;
}

export enum MappingEvent {
  LOCATION_LOADED,
  ZOOM_IN, ZOOM_OUT, ZOOM_TO_ORIGINAL

}
